/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState } from "react";
import { Modal, Box, TextField } from "@mui/material";
import {
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  TxtInput,
  SelectPosition,
} from "../../../components";

import { toast } from "react-toastify";
import { AdminUserModel } from "../../../redux/types";
import { useMutation, useQuery } from "@apollo/client";
import {
  CreateAdminUserInput,
  EditAdminUserInput,
} from "../../../types/graphQlQuery.interface";
import {
  CREATE_ADMIN_USER,
  EDIT_ADMIN_USER,
} from "../../../apolloClient/operation/mutations";
import { GET_ALL_PERMISSION_TEMPLATES } from "../../../apolloClient/operation/querys/userQuery";
import { logEvent, USER_EVENTS } from "../../../utils/amplitude";
import styled from "@emotion/styled";

// Styled Components
const PageWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: #f8f9fa;
  padding: 16px;

  @media only screen and (min-width: 768px) {
    padding: 24px;
  }
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;

  @media only screen and (min-width: 768px) {
    padding: 32px;
  }
`;

const FormTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 24px 0;

  @media only screen and (min-width: 768px) {
    font-size: 28px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 6px;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media only screen and (min-width: 768px) {
    flex-direction: row;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 12px;
  margin-top: 24px;

  @media only screen and (min-width: 768px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: #4b87ed;
  color: white;

  &:hover {
    background-color: #3a6fd8;
  }

  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: white;
  color: #374151;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: #f3f4f6;
  }

  &:active {
    background-color: #e5e7eb;
  }

  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

const SectionTitle = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 24px 0 12px 0;
`;

const TemplateButton = styled.button<{ selected?: boolean }>`
  padding: 6px 14px;
  border: 1px solid ${({ selected }) => (selected ? "#4b87ed" : "#e5e7eb")};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ selected }) => (selected ? "#dbeafe" : "white")};
  color: ${({ selected }) => (selected ? "#1e40af" : "#374151")};

  &:hover {
    background-color: ${({ selected }) => (selected ? "#dbeafe" : "#f3f4f6")};
  }
`;

const LinkText = styled.span`
  color: #4b87ed;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
`;

interface CreateUserProps {
  onTapAdd: Function;
  onFetchUsers: Function;
  editUser?: AdminUserModel;
  roles: string[];
}

export const CreateUser: React.FC<CreateUserProps> = ({
  onTapAdd,
  onFetchUsers,
  editUser,
  roles,
}) => {
  const [isAdded, setIsAdded] = useState(false);
  // Fetch permission templates
  const { data: templatesData, refetch: refetchTemplates } = useQuery(GET_ALL_PERMISSION_TEMPLATES, {
    variables: { limit: 10, offset: 0 },
  });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplateNameInput, setShowTemplateNameInput] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const [firstName, setFirstName] = useState(editUser?.firstName || "");
  const [lastName, setLastName] = useState(editUser?.lastName || "");
  const [email, setEmail] = useState(editUser?.email || "");
  const [phone, setPhone] = useState(editUser?.phone || "");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(editUser?.Status || 0);
  const [role, setRole] = useState(editUser?.role || "");
  const [permissions, setPermissions] = useState<{ [key: string]: string[] }>({
    ANALYTICS_DASHBOARD: [],
    LEADS: [],
    FILE_MANAGER: [],
    PROJECTS: [],
    PROJECT_ASSIGNMENT: [],
    CUSTOMERS: [],
    WAITLIST: [],
    USERS: [],
    INSURANCE_PROVIDER: [],
    PRODUCT_SYSTEM: [],
    MANAGE_WORKFLOW: [],
    SALES_ESTIMATE_CALCULATOR: [],
    NOTIFICATIONS: [],
  });

  const [isAddingNewRole, setIsAddingNewRole] = useState(false);

  const handleToggleRoleInput = () => {
    setIsAddingNewRole(!isAddingNewRole);
    setSelectedRole("");
    setNewRole("");
  };

  const [selectedRole, setSelectedRole] = useState<string>("");
  const [newRole, setNewRole] = useState<string>("");

  const [createAdminUser] = useMutation<CreateAdminUserInput>(
    CREATE_ADMIN_USER,
    {
      onCompleted: () => {
        toast("User created successfully!", { type: "success" });
        
        // Track user creation
        const finalRole = newRole || selectedRole;
        const permissionCount = Object.values(permissions).flat().length;
        logEvent(USER_EVENTS.USER_CREATED, {
          user_email: email,
          user_role: finalRole,
          has_custom_permissions: permissionCount > 0,
          permission_count: permissionCount
        });
        
        setIsAdded(false);
        onFetchUsers();
        refetchTemplates();
      },
      onError: (error) => {
        toast("Error creating user!", { type: "error" });
        console.error("Error creating user:", error);
      },
    }
  );

  // Track selected template
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  // Apply template permissions
  const handleTemplateClick = (template: any) => {
    if (selectedTemplate === template.templateName) {
      // Deselect: remove all permissions from template
      setSelectedTemplate(null);
      setPermissions({
        ANALYTICS_DASHBOARD: [],
        LEADS: [],
        FILE_MANAGER: [],
        PROJECTS: [],
        PROJECT_ASSIGNMENT: [],
        CUSTOMERS: [],
        WAITLIST: [],
        USERS: [],
        INSURANCE_PROVIDER: [],
        PRODUCT_SYSTEM: [],
        MANAGE_WORKFLOW: [],
        SALES_ESTIMATE_CALCULATOR: [],
        NOTIFICATIONS: [],
      });
    } else {
      // Select: apply all permissions from template
      setSelectedTemplate(template.templateName);
      const newPermissions: { [key: string]: string[] } = {
        ANALYTICS_DASHBOARD: [],
        LEADS: [],
        FILE_MANAGER: [],
        PROJECTS: [],
        PROJECT_ASSIGNMENT: [],
        CUSTOMERS: [],
        WAITLIST: [],
        USERS: [],
        INSURANCE_PROVIDER: [],
        PRODUCT_SYSTEM: [],
        MANAGE_WORKFLOW: [],
        SALES_ESTIMATE_CALCULATOR: [],
        NOTIFICATIONS: [],
      };
      template.permissions.forEach((perm: any) => {
        // Parent permissions
        newPermissions[perm.name] = perm.permission ? [...perm.permission] : [];
        // Sub-section permissions
        if (Array.isArray(perm.subSections)) {
          perm.subSections.forEach((sub: any) => {
            if (Array.isArray(sub.permission)) {
              sub.permission.forEach((p: string) => {
                newPermissions[perm.name].push(`${sub.name}_${p}`);
              });
            }
          });
        }
      });
      setPermissions(newPermissions);
    }
  };

  const [editAdminUser] = useMutation<EditAdminUserInput>(EDIT_ADMIN_USER, {
    onCompleted: () => {
      toast("User updated successfully!", { type: "success" });
      
      // Track user update
      const fieldsUpdated = [];
      if (firstName !== editUser?.firstName) fieldsUpdated.push('firstName');
      if (lastName !== editUser?.lastName) fieldsUpdated.push('lastName');
      if (phone !== editUser?.phone) fieldsUpdated.push('phone');
      if (role !== editUser?.role) fieldsUpdated.push('role');
      if (status !== editUser?.Status) fieldsUpdated.push('status');
      if (JSON.stringify(permissions) !== JSON.stringify(editUser?.permissions)) {
        fieldsUpdated.push('permissions');
      }
      
      if (editUser) {
        logEvent(USER_EVENTS.USER_UPDATED, {
          user_id: editUser.id,
          user_email: email,
          fields_updated: fieldsUpdated
        });
      }
      
      setIsAdded(false);
      onFetchUsers();
    },
    onError: (error) => {
      toast("Error updating user!", { type: "error" });
      console.error("Error updating user:", error);
    },
  });

  useEffect(() => {
    if (editUser) {
      setFirstName(editUser.firstName || "");
      setLastName(editUser.lastName || "");
      setEmail(editUser.email || "");
      setPhone(editUser.phone || "");
      setRole(editUser.role || "");
      setStatus(editUser.Status || 0);

      const userPermissions: { [key: string]: string[] } = {};
      editUser.permissions?.forEach((permission) => {
        const { name, permission: parentPermissions, subSections } = permission;

        // Add parent permissions
        userPermissions[name] = parentPermissions || [];

        // Add sub-section permissions if subSections is an array
        if (Array.isArray(subSections)) {
          subSections.forEach((subSection) => {
            const subSectionName = subSection.name;
            const subSectionPermissions = subSection.permission || [];
            subSectionPermissions.forEach((perm) => {
              userPermissions[name] = [
                ...(userPermissions[name] || []),
                `${subSectionName}_${perm}`,
              ];
            });
          });
        }
      });

      setPermissions(userPermissions);
    }
  }, [editUser]);

  useEffect(() => {
    if (isAdded) {
      setIsAdded(false);
      onTapAdd();
    }
  }, []);

  const handlePermissionChange = (section: string, permission: string) => {
    setPermissions((prev) => {
      const updatedPermissions = { ...prev };
      if (updatedPermissions[section]?.includes(permission)) {
        updatedPermissions[section] = updatedPermissions[section].filter(
          (perm) => perm !== permission
        );
      } else {
        updatedPermissions[section] = [
          ...(updatedPermissions[section] || []),
          permission,
        ];
      }
      return updatedPermissions;
    });
  };

  // const handleSelectAllByPermissionType = (
  //   permissionType: string,
  //   isSelected: boolean
  // ) => {
  //   setPermissions((prev) => {
  //     const updatedPermissions = { ...prev };

  //     const sections = [
  //       "LEADS",
  //       "PROJECTS",
  //       "WAITLIST",
  //       "INSURANCE_PROVIDER",
  //       "USERS",
  //       "PRODUCT_SYSTEM",
  //       "MANAGE_WORKFLOW",
  //     ];
  //     sections.forEach((section) => {
  //       if (!updatedPermissions[section]) {
  //         updatedPermissions[section] = [];
  //       }
  //     });

  //     sections.forEach((section) => {
  //       if (isSelected) {
  //         if (!updatedPermissions[section].includes(permissionType)) {
  //           updatedPermissions[section] = [
  //             ...updatedPermissions[section],
  //             permissionType,
  //           ];
  //         }
  //       } else {
  //         updatedPermissions[section] = updatedPermissions[section].filter(
  //           (perm) => perm !== permissionType
  //         );
  //       }
  //     });

  //     return updatedPermissions;
  //   });
  // };

  const subPermissions = {
    PROJECTS: [
      "PROJECT_MEASUREMENTS",
      "MATERIALS",
      "CONTRACTORS",
      "NOTES",
      "ESTIMATE",
      "INSTALLATION_DETAILS",
      "PAYMENT_DETAILS",
      "DOCUMENTS",
      "INVOICE",
      "AFFIDAVIT",
      "COC",
      "SETTINGS",
    ].map((subSection) => subSection.toUpperCase()),
    // Add sub-permissions for other sections if needed
  };

  const formatPermissions = () => {
    return Object.entries(permissions).map(([section, perms]) => {
      const subSections =
        subPermissions[section]?.map((subSection) => ({
          name: subSection,
          permission: perms
            .filter((perm) => perm.startsWith(`${subSection}_`)) // Match the full sub-section name
            .map((perm) => perm.substring(perm.lastIndexOf("_") + 1)), // Extract only the part after the last underscore (READ/WRITE)
        })) || []; // Include all subsections, even if no permissions are selected

      return {
        name: section,
        permission: perms.filter((perm) => !perm.includes("_")), // Parent permissions
        ...(subSections.length > 0 && { subSections }), // Include subSections only if they exist
      };
    });
  };

  const onTapCreateUser = async () => {
    const finalRole = newRole || selectedRole;

    if (
      firstName === "" ||
      lastName === "" ||
      email === "" ||
      password === "" ||
      phone === "" ||
      finalRole === ""
    ) {
      toast("Please enter mandatory fields!", {
        type: "error",
      });
      return;
    }
    // Always show modal to ask about saving as template
    setShowTemplateModal(true);
  };

  // Handler for modal response
  const handleTemplateModalResponse = (saveAsTemplate: boolean) => {
    setShowTemplateModal(false);
    if (saveAsTemplate) {
      setShowTemplateNameInput(true);
    } else {
      // Call mutation without template name
      createAdminUser({
        variables: {
          input: {
            email,
            firstName,
            lastName,
            phone,
            password,
            role: newRole || selectedRole,
            permissions: formatPermissions(),
          },
        },
      });
    }
  };

  // Handler for saving with template name
  const handleSaveWithTemplateName = () => {
    setShowTemplateNameInput(false);
    createAdminUser({
      variables: {
        input: {
          email,
          firstName,
          lastName,
          phone,
          password,
          role: newRole || selectedRole,
          permissions: formatPermissions(),
          saveAsTemplateName: templateName,
        },
      },
    });
    setTemplateName("");
  };

  const onTapSaveUser = async () => {
    if (firstName === "" || lastName === "" || phone === "") {
      toast("Please enter mandatory fields!", {
        type: "error",
      });
      return;
    }

    const formattedPermissions = formatPermissions();

    try {
      await editAdminUser({
        variables: {
          input: {
            id: editUser.id,
            email,
            firstName,
            lastName,
            phone,
            role,
            permissions: formattedPermissions,
            status,
          },
        },
      });
    } catch (error) {
      toast("Error updating user!", {
        type: "error",
      });
    }
  };

  const handleSelectAllSubPermissions = (
    section: string,
    type: string,
    isSelected: boolean
  ) => {
    if (!subPermissions[section]) return; // Ensure subPermissions[section] is defined

    setPermissions((prev) => {
      const updatedPermissions = { ...prev };
      const subSectionPermissions = subPermissions[section].map(
        (subSection) => `${subSection}_${type}`
      );

      if (isSelected) {
        updatedPermissions[section] = [
          ...(updatedPermissions[section] || []),
          ...subSectionPermissions,
        ];
      } else {
        updatedPermissions[section] = updatedPermissions[section]?.filter(
          (perm) => !subSectionPermissions.includes(perm)
        );
      }

      return updatedPermissions;
    });
  };

  const renderSubPermissions = (section: string) => {
    return (
      <Box sx={{ width: "100%" }}>
        {subPermissions[section]?.map((subSection) => (
          <Box 
            key={subSection}
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              py: 1,
              px: 2,
              borderBottom: "1px solid #f3f4f6",
              "&:last-child": { borderBottom: "none" }
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#4b5563", flex: 1 }}>
              {subSection}
            </Typography>
            <Box sx={{ display: "flex", gap: { xs: 1, sm: 3 } }}>
              {["READ", "WRITE"].map((type) => (
                <Checkbox
                  key={type}
                  size="small"
                  checked={permissions[section]?.includes(
                    `${subSection}_${type}`
                  )}
                  onChange={() =>
                    handlePermissionChange(section, `${subSection}_${type}`)
                  }
                  sx={{ padding: { xs: "4px", sm: "8px" } }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderPermissionsAccordion = () => {
    const sections = [
      "ANALYTICS_DASHBOARD",
      "LEADS",
      "FILE_MANAGER",
      "PROJECTS",
      "PROJECT_ASSIGNMENT",
      "CUSTOMERS",
      "MANAGE_WORKFLOW",
      "WAITLIST",
      "INSURANCE_PROVIDER",
      "USERS",
      "PRODUCT_SYSTEM",
      "SALES_ESTIMATE_CALCULATOR",
      "NOTIFICATIONS",
    ];

    return sections.map((section) => {
      const hasSubSections = subPermissions[section]?.length > 0;

      return (
        <Accordion 
          key={section} 
          expanded={hasSubSections ? undefined : false}
          sx={{
            boxShadow: "none",
            border: "1px solid #e5e7eb",
            borderRadius: "8px !important",
            mb: 1,
            "&:before": { display: "none" },
            "&.Mui-expanded": { margin: "0 0 8px 0" },
          }}
        >
          <AccordionSummary
            expandIcon={hasSubSections ? <ExpandMoreIcon /> : null}
            aria-controls={`${section}-content`}
            id={`${section}-header`}
            onClick={(e) => {
              if (!hasSubSections) e.stopPropagation();
            }}
            sx={{
              minHeight: { xs: 48, sm: 56 },
              px: { xs: 1, sm: 2 },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                margin: { xs: "8px 0", sm: "12px 0" },
              }
            }}
          >
            <Typography sx={{ 
              fontWeight: 500, 
              flex: 1, 
              fontSize: { xs: 12, sm: 14 },
              color: "#374151",
              wordBreak: "break-word"
            }}>
              {section.replace(/_/g, " ")}
            </Typography>
            <Box sx={{ display: "flex", gap: { xs: 1, sm: 3 } }}>
              {["READ", "WRITE"].map((type) => (
                <Checkbox
                  key={type}
                  size="small"
                  checked={permissions[section]?.includes(type)}
                  indeterminate={
                    hasSubSections &&
                    subPermissions[section]?.some((subSection) =>
                      permissions[section]?.includes(`${subSection}_${type}`)
                    ) &&
                    !subPermissions[section]?.every((subSection) =>
                      permissions[section]?.includes(`${subSection}_${type}`)
                    )
                  }
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    handlePermissionChange(section, type);
                    if (hasSubSections) {
                      handleSelectAllSubPermissions(
                        section,
                        type,
                        e.target.checked
                      );
                    }
                  }}
                  sx={{ padding: { xs: "4px", sm: "8px" } }}
                />
              ))}
            </Box>
          </AccordionSummary>
          {hasSubSections && (
            <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 0 }}>
              {renderSubPermissions(section)}
            </AccordionDetails>
          )}
        </Accordion>
      );
    });
  };

  const hostContainer = () => {
    return (
      <PageWrapper>
        <FormCard>
          <FormTitle>{editUser ? "Edit User" : "Create New User"}</FormTitle>

          <FormRow>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>First Name *</FormLabel>
              <TxtInput
                value={firstName}
                placeholder="Enter first name"
                onChange={setFirstName}
                background="white"
                width="100%"
              />
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Last Name *</FormLabel>
              <TxtInput
                value={lastName}
                placeholder="Enter last name"
                onChange={setLastName}
                background="white"
                width="100%"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Email *</FormLabel>
              <TxtInput
                disable={editUser ? true : false}
                value={email}
                placeholder="Enter email"
                onChange={setEmail}
                background="white"
                width="100%"
              />
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Phone *</FormLabel>
              <TxtInput
                value={phone}
                placeholder="Enter phone"
                onChange={setPhone}
                background="white"
                width="100%"
              />
            </FormGroup>
          </FormRow>

          {!editUser && (
            <FormGroup>
              <FormLabel>Password *</FormLabel>
              <TxtInput
                value={password}
                placeholder="Enter password"
                onChange={setPassword}
                background="white"
                width="100%"
              />
            </FormGroup>
          )}

          <FormGroup>
            <FormLabel>Role *</FormLabel>
            {editUser ? (
              <Box sx={{ 
                padding: "10px 14px", 
                backgroundColor: "#f3f4f6", 
                borderRadius: "8px",
                fontSize: 14,
                color: "#374151"
              }}>
                {editUser.role}
              </Box>
            ) : (
              <>
                {!isAddingNewRole ? (
                  <>
                    <SelectPosition
                      value={selectedRole}
                      onHandleChange={setSelectedRole}
                      roles={roles}
                    />
                    <Box sx={{ mt: 1 }}>
                      <LinkText onClick={handleToggleRoleInput}>
                        + Add a new role
                      </LinkText>
                    </Box>
                  </>
                ) : (
                  <>
                    <TxtInput
                      value={newRole}
                      placeholder="Enter new role name"
                      onChange={setNewRole}
                      background="white"
                      width="100%"
                    />
                    <Box sx={{ mt: 1 }}>
                      <LinkText onClick={handleToggleRoleInput}>
                        ← Select from existing roles
                      </LinkText>
                    </Box>
                  </>
                )}
              </>
            )}
          </FormGroup>

          {/* Permission Templates */}
          {templatesData?.getAllPermissionTemplates?.length > 0 && (
            <FormGroup>
              <SectionTitle>Permission Templates</SectionTitle>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {templatesData.getAllPermissionTemplates.map((template: any) => (
                  <TemplateButton
                    key={template.templateName}
                    selected={selectedTemplate === template.templateName}
                    onClick={() => handleTemplateClick(template)}
                  >
                    {template.templateName}
                  </TemplateButton>
                ))}
              </Box>
            </FormGroup>
          )}

          {/* Permissions */}
          <SectionTitle>Permissions</SectionTitle>
          <Box sx={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            gap: { xs: 2.5, sm: 4.5 }, 
            mb: 1,
            pr: { xs: 1.5, sm: 2.5 },
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 600,
            color: "#6b7280"
          }}>
            <span>READ</span>
            <span>WRITE</span>
          </Box>
          {renderPermissionsAccordion()}

          <ButtonRow>
            <SecondaryButton onClick={() => onTapAdd()}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                if (editUser) {
                  onTapSaveUser();
                } else {
                  onTapCreateUser();
                }
              }}
              style={editUser ? { backgroundColor: "#f59e0b" } : {}}
            >
              {editUser ? "Save Changes" : "Create User"}
            </PrimaryButton>
          </ButtonRow>
        </FormCard>
      </PageWrapper>
    );
  };

  return (
    <>
      {hostContainer()}
      {/* Modal for save as template */}
      <Modal open={showTemplateModal} onClose={() => setShowTemplateModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            p: 4,
            borderRadius: "12px",
            minWidth: 340,
            maxWidth: "90vw",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#1f2937", mb: 2 }}>
            Save as Template?
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 3 }}>
            Would you like to save these permissions as a template for future use?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <SecondaryButton
              onClick={() => handleTemplateModalResponse(false)}
              style={{ minWidth: 80 }}
            >
              No
            </SecondaryButton>
            <PrimaryButton
              onClick={() => handleTemplateModalResponse(true)}
              style={{ minWidth: 80 }}
            >
              Yes
            </PrimaryButton>
          </Box>
        </Box>
      </Modal>
      {/* Modal for template name input */}
      <Modal open={showTemplateNameInput} onClose={() => setShowTemplateNameInput(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            p: 4,
            borderRadius: "12px",
            minWidth: 340,
            maxWidth: "90vw",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#1f2937", mb: 1 }}>
            Name Your Template
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 2 }}>
            Enter a name to identify this permission template
          </Typography>
          <TextField
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Sales Team, Admin"
            sx={{ 
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#4b87ed",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#4b87ed",
                },
              }
            }}
            inputProps={{ style: { padding: "12px 14px" } }}
          />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <SecondaryButton
              onClick={() => setShowTemplateNameInput(false)}
              style={{ minWidth: 80 }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              disabled={!templateName}
              onClick={handleSaveWithTemplateName}
              style={{ 
                minWidth: 140,
                opacity: !templateName ? 0.5 : 1,
                cursor: !templateName ? "not-allowed" : "pointer"
              }}
            >
              Save & Create
            </PrimaryButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
